import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Switch,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  CircularProgress,
  Paper,
  Chip,
  Autocomplete,
  Checkbox,
  Radio,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Tooltip,
} from '@mui/material';
import SearchableSelect from 'components/Forms/searchable/SearchableSelect';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import TranslationInput from '../../../components/PosMenu/TranslationInput';
import FieldSavingIndicator from '../../../components/PosMenu/FieldSavingIndicator';
import { getUniqueSourceOptions } from '../../../utils/posMenuNestedSources';
import { validateSelectionLimits } from '../../../utils/posMenuSelectionLimits';
import ConnectedProductsPickerDialog from '../../../components/PosMenu/ConnectedProductsPickerDialog';
import ModifierGroupOptionsManager from '../../PosMenu/Overview/ModifierGroupOptionsManager';
import {
  createModifierGroup,
  updateModifierGroup,
  deleteModifierGroup,
  createItemModifierGroup,
  deleteItemModifierGroup,
  bulkCreateModifierGroupOptions,
  createItem,
} from '../../../redux/actions/posMenu';
import { priceTransform, formatNumber } from '../../../helpers/formPrice';
import {
  DEFAULT_MODIFIER_GROUP,
  getTranslationValue,
  PRICE_TYPE_OPTIONS,
  IN_ORDER_VIEW_LAYOUT_OPTIONS,
} from '../../../constants/posMenu';
import { isPosProviderConnected } from '../../../utils/checkAccessDenied';
import usePosMenuMap from '../hooks/usePosMenuMap';
import useScheduledTimeouts from '../hooks/useScheduledTimeouts';
import MenuLoadError from '../../../components/PosMenu/MenuLoadError';

// Helper function to find all descendant items (options and nested options) of a modifier group
const getMgDescendants = (mgId, menuMap) => {
  const descendants = new Set();
  const visited = new Set();

  const findDescendants = (modifierGroupId) => {
    if (visited.has(modifierGroupId)) return;
    visited.add(modifierGroupId);

    const mg = menuMap?.modifierGroups?.[modifierGroupId];
    if (!mg?.optionIds) return;

    // Add all direct options
    mg.optionIds.forEach((optionId) => {
      descendants.add(optionId);

      // For each option, check if it has modifier groups and recurse
      const optionItem = menuMap?.items?.[optionId];
      if (optionItem?.modifierGroupIds) {
        optionItem.modifierGroupIds.forEach((nestedMgId) => {
          findDescendants(nestedMgId);
        });
      }
    });
  };

  findDescendants(mgId);
  return descendants;
};

const ModifierGroupEdit = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const isNew = id === 'new';

  const { menuMap, internalMenuId } = useSelector(
    (state) => state.posMenu
  );
  const {
    items: providerItems,
    loading: providersLoading,
    providerId,
  } = useSelector((state) => state.providers);

  const [formData, setFormData] = useState(DEFAULT_MODIFIER_GROUP);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldSavingStatus, setFieldSavingStatus] = useState({});
  const [isUpdatingProducts, setIsUpdatingProducts] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const saveTimeoutsRef = useRef({});
  const scheduleTimeout = useScheduledTimeouts();
  const lastInitializedIdRef = useRef(null);

  // State for nested modifier group creation
  const [isNested, setIsNested] = useState(false);
  const [sourceMgIds, setSourceMgIds] = useState([]); // Array of selected source modifier group IDs
  const [nestedOptions, setNestedOptions] = useState([]); // [{name: '', prices: {sourceOptionId: price}}]
  const [newNestedOptionName, setNewNestedOptionName] = useState('');
  const [newNestedOptionPrices, setNewNestedOptionPrices] = useState({}); // {sourceOptionId: price}
  const [attachAfterCreate, setAttachAfterCreate] = useState(true); // Whether to attach nested MGs to source options

  // Get business ID from localStorage
  const businessId = JSON.parse(localStorage.getItem('businessInfo'))?.id;

  // Check if providers have been loaded
  const providersLoaded = providerId !== null || providerItems?.length > 0;

  // Check if POS is connected
  const isPosConnected = isPosProviderConnected(providerItems);

  // Get current modifier group from menuMap
  const modifierGroup =
    !isNew && menuMap?.modifierGroups?.[id] ? menuMap.modifierGroups[id] : null;
  const isEditing = !isNew && modifierGroup?._id;

  const { loadStatus: menuLoadStatus, retry: retryMenuLoad } =
    usePosMenuMap(businessId);

  // Initialize form data when modifier group changes
  useEffect(() => {
    const currentId = modifierGroup?._id || null;

    // Reset the ref when entity becomes null (navigating away)
    // This ensures fresh initialization when returning to the same entity
    if (!currentId) {
      lastInitializedIdRef.current = null;
      // Reset form data when creating new modifier group
      if (isNew) {
        setFormData(DEFAULT_MODIFIER_GROUP);
      }
      return;
    }

    if (currentId === lastInitializedIdRef.current) {
      return;
    }
    lastInitializedIdRef.current = currentId;

    if (modifierGroup && modifierGroup._id) {
      setFormData(modifierGroup);
    }
  }, [modifierGroup, isNew]);

  // Sync formData with Redux state when it changes externally
  // This handles cases where Redux state is updated by other actions
  // (e.g., adding options, other components modifying the same entity)
  useEffect(() => {
    if (!modifierGroup?._id || isNew) return;

    // Only sync if we're on the same entity that was initialized
    if (modifierGroup._id !== lastInitializedIdRef.current) return;

    // Check if any fields in the current formData differ from Redux
    // and update them while preserving user's unsaved local changes
    setFormData((prev) => {
      // Only update if the Redux data is actually different
      // This prevents unnecessary re-renders
      const needsUpdate =
        prev.name?.en !== modifierGroup.name?.en ||
        prev.optionIds?.length !== modifierGroup.optionIds?.length ||
        JSON.stringify(prev.optionIds) !==
          JSON.stringify(modifierGroup.optionIds) ||
        JSON.stringify(prev.preselectOptions) !==
          JSON.stringify(modifierGroup.preselectOptions);

      if (!needsUpdate) return prev;

      return {
        ...prev,
        ...modifierGroup,
      };
    });
  }, [modifierGroup, isNew]);

  // Cleanup timeouts on unmount AND when entity changes
  useEffect(() => {
    return () => {
      // Send debounced saves now instead of dropping them
      Object.values(saveTimeoutsRef.current).forEach((pending) => {
        if (!pending) return;
        clearTimeout(pending.timeoutId);
        pending.flush();
      });
      saveTimeoutsRef.current = {};
    };
  }, [id]); // Re-run cleanup when id changes

  const handleSave = async () => {
    try {
      if (!formData.name?.en?.trim()) {
        toast.error('Name is Required');
        return;
      }
      const limitsError = validateSelectionLimits(formData);
      if (limitsError) {
        toast.error(limitsError);
        return;
      }

      setIsSaving(true);

      if (isEditing) {
        const updateData = {
          name: formData.name,
          title: formData.title,
          description: formData.description,
          min: formData.min ?? null,
          max: formData.max ?? null,
          free: formData.free ?? null,
          isOnePrice: formData.isOnePrice,
          priceType: formData.priceType ?? null,
          tags: formData.tags,
          sort: formData.sort,
          canSelectMultipleOptions: formData.canSelectMultipleOptions,
          maxAnySingleModifierItems: formData.maxAnySingleModifierItems ?? null,
          maxTotalNumberOfModifierItems:
            formData.maxTotalNumberOfModifierItems ?? null,
          minAnySingleModifierItems: formData.minAnySingleModifierItems ?? null,
          minTotalNumberOfModifierItems:
            formData.minTotalNumberOfModifierItems ?? null,
          hideNameOnOrder: formData.hideNameOnOrder ?? false,
          showNestedLayout: formData.showNestedLayout ?? 'default',
          inOrderViewLayout: formData.inOrderViewLayout ?? null,
          applyConditionWithMultiplier:
            formData.applyConditionWithMultiplier ?? null,
          conditionIsAppliedForProduct:
            formData.conditionIsAppliedForProduct ?? null,
          applyValidationToItemsActualQuantity:
            formData.applyValidationToItemsActualQuantity ?? null,
          sortParentOptionsByThisOptionsSorting:
            formData.sortParentOptionsByThisOptionsSorting ?? null,
          groupAllParentsByThisOption:
            formData.groupAllParentsByThisOption ?? null,
          preselectOptions: formData.preselectOptions,
        };
        await dispatch(updateModifierGroup(modifierGroup._id, updateData));
        toast.success('Modifier group updated successfully');
      } else {
        if (!internalMenuId) {
          toast.error(
            'Menu information is not loaded. Please refresh the page.'
          );
          return;
        }

        // Handle nested modifier creation
        if (isNested && sourceMgIds.length > 0) {
          // Filter out options with empty names
          const validOptions = nestedOptions.filter((opt) => opt.name?.trim());

          if (validOptions.length === 0) {
            toast.error(
              'Please add at least one option with a name for nested modifier groups'
            );
            setIsSaving(false);
            return;
          }

          const allSourceOptionIds = getUniqueSourceOptions(
            sourceMgIds,
            menuMap?.modifierGroups
          ).map(({ optId }) => optId);

          if (allSourceOptionIds.length === 0) {
            toast.error('Selected source modifier groups have no options');
            setIsSaving(false);
            return;
          }

          // Create all MGs in parallel for speed
          const mgCreationPromises = allSourceOptionIds.map(
            async (sourceOptionId) => {
              const sourceOption = menuMap?.items?.[sourceOptionId];
              const sourceOptionName =
                getTranslationValue(sourceOption?.name) || 'Option';

              // 1. Create new MG - name stays as user set, title gets option name appended
              const titleBase = formData.title?.en || formData.name.en;
              const newMgTitle = `${titleBase} - ${sourceOptionName}`;
              const newMg = await dispatch(
                createModifierGroup({
                  name: { en: formData.name.en },
                  title: { en: newMgTitle },
                  description: formData.description,
                  min: formData.min ?? null,
                  max: formData.max ?? null,
                  free: formData.free ?? null,
                  isOnePrice: formData.isOnePrice,
                  priceType: formData.priceType ?? null,
                  tags: formData.tags,
                  sort: formData.sort,
                  canSelectMultipleOptions: formData.canSelectMultipleOptions,
                  maxAnySingleModifierItems:
                    formData.maxAnySingleModifierItems ?? null,
                  maxTotalNumberOfModifierItems:
                    formData.maxTotalNumberOfModifierItems ?? null,
                  minAnySingleModifierItems:
                    formData.minAnySingleModifierItems ?? null,
                  minTotalNumberOfModifierItems:
                    formData.minTotalNumberOfModifierItems ?? null,
                  hideNameOnOrder: formData.hideNameOnOrder ?? false,
                  showNestedLayout: formData.showNestedLayout ?? 'default',
                  inOrderViewLayout: formData.inOrderViewLayout ?? null,
                  applyConditionWithMultiplier:
                    formData.applyConditionWithMultiplier ?? null,
                  conditionIsAppliedForProduct:
                    formData.conditionIsAppliedForProduct ?? null,
                  applyValidationToItemsActualQuantity:
                    formData.applyValidationToItemsActualQuantity ?? null,
                  sortParentOptionsByThisOptionsSorting:
                    formData.sortParentOptionsByThisOptionsSorting ?? null,
                  groupAllParentsByThisOption:
                    formData.groupAllParentsByThisOption ?? null,
                  menu: internalMenuId,
                })
              );

              // 2. Create all items in parallel for this MG
              const itemCreationPromises = validOptions.map((opt) => {
                const priceForThisMg = opt.prices?.[sourceOptionId] || 0;
                return dispatch(
                  createItem({
                    name: { en: opt.name },
                    title: { en: `${opt.name} ${newMgTitle}` },
                    description: { en: '' },
                    price: priceForThisMg,
                    priceType: null,
                    tax: null,
                    sort: 0,
                    tags: [],
                    isOnePrice: false,
                    image: { url: '', fileId: '' },
                    soldOut: { status: 'available', soldOutDate: null },
                    onItsOwn: false,
                    isSizeOption: false,
                    kitchenInstruction: { en: '' },
                    recipe: { en: '' },
                    minAllowedQuantity: null,
                    maxAllowedQuantity: null,
                    minTotalNumberOfModifierGroups: null,
                    maxTotalNumberOfModifierGroups: null,
                    minTotalNumberOfModifierItems: null,
                    maxTotalNumberOfModifierItems: null,
                    isPopular: false,
                    scheduleIds: [],
                    printerTags: [],
                    menu: internalMenuId,
                    hideNameOnOrder: false,
                    showNestedLayout: 'default',
                    inOrderViewLayout: null,
                  })
                );
              });

              const createdItems = await Promise.all(itemCreationPromises);

              // 3. Attach all items to MG at once with correct sorting
              await dispatch(
                bulkCreateModifierGroupOptions(
                  newMg._id,
                  createdItems.map((item, index) => ({
                    optionId: item._id,
                    sort: index,
                  })),
                  internalMenuId
                )
              );

              // 4. Attach this MG to the source option (e.g., Small, Medium, Large) - only if attachAfterCreate is true.
              // No sort: the backend appends it after the option's existing groups.
              if (attachAfterCreate) {
                await dispatch(
                  createItemModifierGroup({
                    itemId: sourceOptionId,
                    modifierGroupId: newMg._id,
                    menuId: internalMenuId,
                  })
                );
              }

              return newMg;
            }
          );

          const results = await Promise.allSettled(mgCreationPromises);
          const failedSourceNames = allSourceOptionIds
            .filter((_, index) => results[index].status === 'rejected')
            .map(
              (id) =>
                getTranslationValue(menuMap?.items?.[id]?.name) || 'Option'
            );
          const createdMgCount = results.length - failedSourceNames.length;

          if (createdMgCount === 0) {
            toast.error(
              results[0].reason?.message ||
                'Failed to create nested modifier groups'
            );
            setIsSaving(false);
            return;
          }

          if (failedSourceNames.length > 0) {
            toast.warning(
              `${createdMgCount} modifier group(s) created. Failed for: ${failedSourceNames.join(
                ', '
              )}. Check those options before retrying, they may be partially set up.`
            );
          } else {
            toast.success(
              attachAfterCreate
                ? `${createdMgCount} modifier group(s) created and attached successfully`
                : `${createdMgCount} modifier group(s) created successfully`
            );
          }
          // Navigate away so a retry can't recreate the groups that succeeded
          history.replace(`/pos-menu/modifier-groups/${sourceMgIds[0]}`);
          return;
        }

        // Regular (non-nested) modifier group creation
        const createData = {
          name: formData.name,
          title: formData.title,
          description: formData.description,
          min: formData.min ?? null,
          max: formData.max ?? null,
          free: formData.free ?? null,
          isOnePrice: formData.isOnePrice,
          priceType: formData.priceType ?? null,
          tags: formData.tags,
          sort: formData.sort,
          canSelectMultipleOptions: formData.canSelectMultipleOptions,
          maxAnySingleModifierItems: formData.maxAnySingleModifierItems ?? null,
          maxTotalNumberOfModifierItems:
            formData.maxTotalNumberOfModifierItems ?? null,
          minAnySingleModifierItems: formData.minAnySingleModifierItems ?? null,
          minTotalNumberOfModifierItems:
            formData.minTotalNumberOfModifierItems ?? null,
          hideNameOnOrder: formData.hideNameOnOrder ?? false,
          showNestedLayout: formData.showNestedLayout ?? 'default',
          inOrderViewLayout: formData.inOrderViewLayout ?? null,
          applyConditionWithMultiplier:
            formData.applyConditionWithMultiplier ?? null,
          conditionIsAppliedForProduct:
            formData.conditionIsAppliedForProduct ?? null,
          applyValidationToItemsActualQuantity:
            formData.applyValidationToItemsActualQuantity ?? null,
          sortParentOptionsByThisOptionsSorting:
            formData.sortParentOptionsByThisOptionsSorting ?? null,
          groupAllParentsByThisOption:
            formData.groupAllParentsByThisOption ?? null,
          menu: internalMenuId,
        };
        const created = await dispatch(createModifierGroup(createData));
        toast.success('Modifier group created successfully');
        // Navigate to the created modifier group's edit page
        history.replace(`/pos-menu/modifier-groups/${created._id}`);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    history.push('/pos-menu/modifier-groups');
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!modifierGroup || !modifierGroup._id) return;

    try {
      setIsDeleting(true);
      await dispatch(deleteModifierGroup(modifierGroup._id));
      // Pending edits of the deleted group must not be flushed on unmount
      Object.values(saveTimeoutsRef.current).forEach(
        (pending) => pending && clearTimeout(pending.timeoutId)
      );
      saveTimeoutsRef.current = {};
      toast.success('Modifier group deleted successfully');
      // isDeleting stays set: the page is left right away
      history.push('/pos-menu/modifier-groups');
    } catch (error) {
      toast.error(error.message || 'An error occurred');
      setIsDeleting(false);
    }
  };

  const handleFieldAutoSave = async (fieldName, value) => {
    if (!isEditing) return;

    // Capture the current entity ID to ensure we're updating the right entity
    const entityId = modifierGroup?._id;
    if (!entityId) return;

    if (saveTimeoutsRef.current[fieldName]) {
      clearTimeout(saveTimeoutsRef.current[fieldName].timeoutId);
      saveTimeoutsRef.current[fieldName] = null;
    }

    if (['min', 'max', 'free'].includes(fieldName)) {
      const limitsError = validateSelectionLimits({
        min: formData.min,
        max: formData.max,
        free: formData.free,
        [fieldName]: value,
      });
      if (limitsError) {
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'error' }));
        toast.error(limitsError);
        return;
      }
    }

    setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'saving' }));

    const debounceTime = fieldName === 'preselectOptions' ? 100 : 300;

    const runSave = async ({ flushing = false } = {}) => {
      try {
        // Verify we're still editing the same entity before saving.
        // A flush on unmount/navigation always targets the captured entity.
        if (!flushing && lastInitializedIdRef.current !== entityId) {
          setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'idle' }));
          return;
        }

        if (fieldName === 'name' && !value?.en?.trim()) {
          setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'error' }));
          toast.error('Name is Required');
          return;
        }

        const updateData = {
          [fieldName]: value,
        };

        // The form already holds this value; writing it back here would
        // overwrite anything typed while the request was in flight
        await dispatch(updateModifierGroup(entityId, updateData));
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'saved' }));

        scheduleTimeout(() => {
          setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'idle' }));
        }, 2000);
      } catch (error) {
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'error' }));
        toast.error(`Failed to save ${fieldName}: ${error.message}`);
      }
    };

    const timeoutId = setTimeout(() => {
      saveTimeoutsRef.current[fieldName] = null;
      runSave();
    }, debounceTime);

    saveTimeoutsRef.current[fieldName] = {
      timeoutId,
      flush: () => runSave({ flushing: true }),
    };
  };

  const handlePickerSave = async (addedIds, removedIds) => {
    if (!modifierGroup?._id) return;
    setIsUpdatingProducts(true);
    const failedConnect = [];
    const failedRemove = [];
    try {
      for (const itemId of addedIds) {
        try {
          // No sort: the backend appends it after the product's existing groups
          await dispatch(
            createItemModifierGroup({
              itemId,
              modifierGroupId: modifierGroup._id,
              menuId: internalMenuId,
            })
          );
        } catch (error) {
          failedConnect.push(itemId);
        }
      }
      for (const itemId of removedIds) {
        try {
          await dispatch(
            deleteItemModifierGroup({
              itemId,
              modifierGroupId: modifierGroup._id,
            })
          );
        } catch (error) {
          failedRemove.push(itemId);
        }
      }

      const connectedCount = addedIds.length - failedConnect.length;
      const removedCount = removedIds.length - failedRemove.length;
      const productNames = (ids) =>
        ids
          .map((id) => getTranslationValue(menuMap?.items?.[id]?.name) || id)
          .join(', ');

      if (failedConnect.length > 0 || failedRemove.length > 0) {
        // Keep the picker open: what succeeded is already applied, so a
        // second save only retries the failed products
        toast.error(
          [
            `${connectedCount} product(s) connected, ${removedCount} removed.`,
            failedConnect.length > 0 &&
              `Couldn't connect: ${productNames(failedConnect)}.`,
            failedRemove.length > 0 &&
              `Couldn't remove: ${productNames(failedRemove)}.`,
          ]
            .filter(Boolean)
            .join(' ')
        );
        return;
      }

      if (addedIds.length > 0 && removedIds.length > 0) {
        toast.success('Connected products updated');
      } else if (addedIds.length > 0) {
        toast.success(
          `${addedIds.length} product(s) connected to this modifier group`
        );
      } else if (removedIds.length > 0) {
        toast.success(
          `${removedIds.length} product(s) removed from this modifier group`
        );
      }
      setPickerOpen(false);
    } finally {
      setIsUpdatingProducts(false);
    }
  };

  const handleQuickRemove = async (productId) => {
    if (!modifierGroup?._id || isUpdatingProducts) return;
    setIsUpdatingProducts(true);
    try {
      await dispatch(
        deleteItemModifierGroup({
          itemId: productId,
          modifierGroupId: modifierGroup._id,
        })
      );
      toast.success('Product removed from this modifier group');
    } catch (error) {
      toast.error(error.message || 'Failed to remove product');
    } finally {
      setIsUpdatingProducts(false);
    }
  };

  const cleanPreselectOptions = (currentOptions) => {
    if (!currentOptions || !modifierGroup?.optionIds) {
      return {};
    }

    const cleanedOptions = {};
    const validOptionIds = new Set(modifierGroup.optionIds);

    for (const [key, selections] of Object.entries(currentOptions)) {
      if (key !== modifierGroup._id) continue;

      const cleanedSelections = (selections || []).filter((sel) =>
        validOptionIds.has(sel._id)
      );

      if (cleanedSelections.length > 0) {
        cleanedOptions[key] = cleanedSelections;
      }
    }

    return cleanedOptions;
  };

  // Show loading while providers are loading
  if (providersLoading || !providersLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show POS not connected message
  if (!isPosConnected) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            POS Not Connected
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Please connect your business to a POS system to access the POS Menu.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Neither an edit nor a new group is shown until the menu has loaded
  if (menuLoadStatus === 'error') {
    return (
      <MenuLoadError
        onRetry={retryMenuLoad}
        onBack={handleBack}
        backLabel="Back to Modifier Groups"
      />
    );
  }
  if (menuLoadStatus !== 'ready') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Just deleted: the store no longer has it, and navigation is under way
  if (isDeleting && !modifierGroup) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show not found if editing but modifier group doesn't exist
  if (!isNew && !modifierGroup && menuMap) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Modifier Groups
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Modifier Group Not Found
          </Typography>
          <Typography color="textSecondary">
            The modifier group you're looking for doesn't exist or has been
            deleted.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Back button */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={handleBack}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          Back to Modifier Groups
        </Button>
      </Box>

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#141414',
          }}
        >
          {isEditing ? 'Edit Modifier Group' : 'New Modifier Group'}
        </Typography>
        <Box>
          {isEditing && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              sx={{ mr: 1 }}
            >
              Delete
            </Button>
          )}
          {!isEditing && (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isSaving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving
                ? 'Saving...'
                : isNested && sourceMgIds.length > 0
                ? `Create ${
                    getUniqueSourceOptions(sourceMgIds, menuMap?.modifierGroups)
                      .length
                  } MG(s)${attachAfterCreate ? ' & Attach' : ''}`
                : 'Save'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <TranslationInput
                label="Name"
                value={formData.name || { en: '', es: '' }}
                onChange={(value) => setFormData({ ...formData, name: value })}
                onBlur={(value) => handleFieldAutoSave('name', value)}
                required
              />
            </Box>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.name} />
            </Box>
          </Box>

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <TranslationInput
                label="Internal Name"
                value={formData.title || { en: '', es: '' }}
                onChange={(value) => setFormData({ ...formData, title: value })}
                onBlur={(value) => handleFieldAutoSave('title', value)}
              />
            </Box>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.title} />
            </Box>
          </Box>

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <TranslationInput
                label="Description"
                value={formData.description || { en: '', es: '' }}
                onChange={(value) =>
                  setFormData({ ...formData, description: value })
                }
                onBlur={(value) => handleFieldAutoSave('description', value)}
                multiline
                rows={3}
              />
            </Box>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.description} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="mg-price-type-basic-label">Price Type</InputLabel>
              <SearchableSelect
                labelId="mg-price-type-basic-label"
                label="Price Type"
                value={formData.priceType || ''}
                displayEmpty
                renderValue={(selected) => {
                  const option = PRICE_TYPE_OPTIONS.find(
                    (o) => o.value === selected
                  );
                  return option?.label || 'None';
                }}
                onChange={(e) => {
                  const newValue = e.target.value || null;
                  setFormData((prev) => ({ ...prev, priceType: newValue }));
                  if (isEditing) {
                    handleFieldAutoSave('priceType', newValue);
                  }
                }}
              >
                {PRICE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <FieldSavingIndicator status={fieldSavingStatus.priceType} />
          </Box>
        </CardContent>
      </Card>

      {/* Nested Modifier Section - Only show in create mode */}
      {isNew && (
        <Card sx={{ mb: 3, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isNested}
                  onChange={(e) => {
                    setIsNested(e.target.checked);
                    if (!e.target.checked) {
                      setSourceMgIds([]);
                      setNestedOptions([]);
                      setNewNestedOptionName('');
                      setNewNestedOptionPrices({});
                      setAttachAfterCreate(true);
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    Is Nested Modifier
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#666' }}>
                    Create multiple modifier groups based on another modifier
                    group's options
                  </Typography>
                </Box>
              }
            />

            {isNested && (
              <Box sx={{ mt: 2 }}>
                {/* Source MG Dropdown - Multi-select */}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 600,
                    color: 'text.secondary',
                  }}
                >
                  Select Source Modifier Group(s)
                </Typography>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={Object.values(menuMap?.modifierGroups || {}).filter(
                    (mg) => (mg.optionIds?.length || 0) > 0
                  )}
                  value={Object.values(menuMap?.modifierGroups || {}).filter(
                    (mg) => sourceMgIds.includes(mg._id)
                  )}
                  onChange={(event, newValue) => {
                    setSourceMgIds(newValue.map((mg) => mg._id));
                    // Reset options and prices when source MGs change
                    setNestedOptions([]);
                    setNewNestedOptionName('');
                    setNewNestedOptionPrices({});
                  }}
                  getOptionLabel={(option) => {
                    const name = getTranslationValue(option.name) || 'Unnamed';
                    const internalName = getTranslationValue(option.title);
                    return internalName ? `${name} (${internalName})` : name;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option._id === value._id
                  }
                  filterOptions={(options, { inputValue }) => {
                    const searchTerm = inputValue.toLowerCase();
                    return options.filter((option) => {
                      const name = (
                        getTranslationValue(option.name) || ''
                      ).toLowerCase();
                      const internalName = (
                        getTranslationValue(option.title) || ''
                      ).toLowerCase();
                      return (
                        name.includes(searchTerm) ||
                        internalName.includes(searchTerm)
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        sourceMgIds.length === 0
                          ? 'Select modifier group(s)...'
                          : ''
                      }
                      size="small"
                      sx={{ bgcolor: 'white' }}
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const name = getTranslationValue(option.name) || 'Unnamed';
                    const internalName = getTranslationValue(option.title);
                    const optionsCount = option.optionIds?.length || 0;
                    return (
                      <li {...props} key={option._id}>
                        <Checkbox
                          checked={selected}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Box>
                          <Typography sx={{ fontWeight: 500 }}>
                            {name}
                            {internalName && (
                              <Typography
                                component="span"
                                sx={{
                                  ml: 0.5,
                                  color: 'text.secondary',
                                  fontWeight: 400,
                                }}
                              >
                                ({internalName})
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {optionsCount} option(s)
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        label={getTranslationValue(option.name) || 'Unnamed'}
                        size="small"
                      />
                    ))
                  }
                  sx={{ mb: 2 }}
                />

                {/* Show selected MG options as chips */}
                {sourceMgIds.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 1,
                        fontWeight: 600,
                        color: 'text.secondary',
                      }}
                    >
                      Source Options (will create one MG per option):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {getUniqueSourceOptions(
                        sourceMgIds,
                        menuMap?.modifierGroups
                      ).map(({ optId, mgNames }) => {
                        const optionItem = menuMap?.items?.[optId];
                        return (
                          <Chip
                            key={optId}
                            label={`${
                              getTranslationValue(optionItem?.name) ||
                              'Unnamed'
                            } (${mgNames.join(', ')})`}
                            size="small"
                            sx={{ bgcolor: '#e3f2fd' }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* Inline Options UI */}
                {sourceMgIds.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 2,
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                      }}
                    >
                      Add Options for Nested MGs
                    </Typography>

                    {/* Inline editable options - two line layout */}
                    <Box>
                      {/* Editable rows */}
                      {nestedOptions.map((opt, index) => {
                        // Collect all source option IDs from all selected MGs
                        const allSourceOptionIds = getUniqueSourceOptions(
                          sourceMgIds,
                          menuMap?.modifierGroups
                        );

                        return (
                          <Box
                            key={index}
                            sx={{
                              mb: 2,
                              p: 1.5,
                              bgcolor: '#f9f9f9',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {/* Line 1: Option Name + Copy + Delete */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1.5,
                              }}
                            >
                              <TextField
                                value={opt.name}
                                onChange={(e) => {
                                  setNestedOptions((prev) =>
                                    prev.map((item, i) =>
                                      i === index
                                        ? { ...item, name: e.target.value }
                                        : item
                                    )
                                  );
                                }}
                                size="small"
                                placeholder="Option name"
                                label="Option Name"
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1 }}
                              />
                              <Tooltip title="Copy option with empty name">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setNestedOptions((prev) => {
                                      const optionToCopy = prev[index];
                                      const newOption = {
                                        name: '', // Empty name
                                        prices: { ...optionToCopy.prices }, // Copy prices
                                      };
                                      // Insert copy after the original
                                      return [
                                        ...prev.slice(0, index + 1),
                                        newOption,
                                        ...prev.slice(index + 1),
                                      ];
                                    });
                                  }}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setNestedOptions((prev) =>
                                    prev.filter((_, i) => i !== index)
                                  );
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            {/* Line 2: Prices for all source options */}
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${Math.min(
                                  allSourceOptionIds.length || 1,
                                  4
                                )}, 1fr)`,
                                gap: 1,
                              }}
                            >
                              {allSourceOptionIds.map(
                                ({ optId, mgNames }) => {
                                  const optionItem = menuMap?.items?.[optId];
                                  const label =
                                    sourceMgIds.length > 1
                                      ? `${
                                          getTranslationValue(
                                            optionItem?.name
                                          ) || 'N/A'
                                        } (${mgNames.join(', ')})`
                                      : getTranslationValue(optionItem?.name) ||
                                        'N/A';
                                  return (
                                    <TextField
                                      key={optId}
                                      label={label}
                                      value={formatNumber(
                                        opt.prices?.[optId] || 0
                                      )}
                                      onChange={(e) => {
                                        const transformed = priceTransform(
                                          e.target.value
                                        );
                                        setNestedOptions((prev) =>
                                          prev.map((item, i) =>
                                            i === index
                                              ? {
                                                  ...item,
                                                  prices: {
                                                    ...item.prices,
                                                    [optId]: transformed,
                                                  },
                                                }
                                              : item
                                          )
                                        );
                                      }}
                                      size="small"
                                      InputLabelProps={{ shrink: true }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            $
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  );
                                }
                              )}
                            </Box>
                          </Box>
                        );
                      })}

                      {/* Add option button */}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setNestedOptions((prev) => [
                            ...prev,
                            { name: '', prices: {} },
                          ]);
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Add Option
                      </Button>
                    </Box>

                    {nestedOptions.length === 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          color: 'text.secondary',
                          fontStyle: 'italic',
                        }}
                      >
                        Click "Add Option" to add options for the nested
                        modifier groups
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Attach option toggle */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={attachAfterCreate}
                      onChange={(e) => setAttachAfterCreate(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Attach to source options after creation
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#666' }}>
                        When unchecked, modifier groups will be created but not
                        attached
                      </Typography>
                    </Box>
                  }
                  sx={{ mt: 2 }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selection Rules */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Selection Rules
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Minimum"
              type="number"
              value={formData.min ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({
                  ...formData,
                  min: val === '' ? null : parseInt(val),
                });
              }}
              onBlur={(e) => {
                const val = e.target.value;
                handleFieldAutoSave('min', val === '' ? null : parseInt(val));
              }}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '0' }}
            />

            <TextField
              label="Maximum"
              type="number"
              value={formData.max ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({
                  ...formData,
                  max: val === '' ? null : parseInt(val),
                });
              }}
              onBlur={(e) => {
                const val = e.target.value;
                handleFieldAutoSave('max', val === '' ? null : parseInt(val));
              }}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '0' }}
            />

            <TextField
              label="Free"
              type="number"
              value={formData.free ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setFormData({
                  ...formData,
                  free: val === '' ? null : parseInt(val),
                });
              }}
              onBlur={(e) => {
                const val = e.target.value;
                handleFieldAutoSave('free', val === '' ? null : parseInt(val));
              }}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '0' }}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontStyle: 'italic',
              mb: 2,
            }}
          >
            Define how many options customers can select from this group
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.canSelectMultipleOptions || false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({
                      ...formData,
                      canSelectMultipleOptions: newValue,
                    });
                    if (isEditing) {
                      handleFieldAutoSave('canSelectMultipleOptions', newValue);
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    Allow Multiple Quantities
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Allows customers to select the same modifier option multiple times using quantity controls.
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.hideNameOnOrder || false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({
                      ...formData,
                      hideNameOnOrder: newValue,
                    });
                    if (isEditing) {
                      handleFieldAutoSave('hideNameOnOrder', newValue);
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    Hide Modifier Group Name on Orders
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Hides the modifier group name from order displays, receipts, and tickets.
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.applyConditionWithMultiplier || false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({
                      ...formData,
                      applyConditionWithMultiplier: newValue,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'applyConditionWithMultiplier',
                        newValue
                      );
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Use Option Multipliers for Selection Rules
                    </Typography>
                    <Tooltip title="- Modifier group must be Multiplier (not Fixed Price)" placement="top" arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Counts the multiplier value of each option when evaluating the group's selection rules.
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.conditionIsAppliedForProduct || false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({
                      ...formData,
                      conditionIsAppliedForProduct: newValue,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'conditionIsAppliedForProduct',
                        newValue
                      );
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    Apply Selection Rules Across Product
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Combines selections for this modifier group across all locations in the product when evaluating its selection rules.
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.applyValidationToItemsActualQuantity || false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({
                      ...formData,
                      applyValidationToItemsActualQuantity: newValue,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'applyValidationToItemsActualQuantity',
                        newValue
                      );
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Weigh Selections by Multipliers
                    </Typography>
                    <Tooltip
                      title={
                        <Box>
                          <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Fixed Price</Typography>
                          <Typography sx={{ fontSize: '12px' }}>- Modifier group must have at least one of:</Typography>
                          <Typography sx={{ fontSize: '12px' }}>  • a nested Parent Multiplier modifier group</Typography>
                          <Typography sx={{ fontSize: '12px' }}>  • a parent Nested Multiplier modifier group</Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Uses the multiplier values selected in the nested modifier group to evaluate this group's selection rules.
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.sortParentOptionsByThisOptionsSorting || false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({
                      ...formData,
                      sortParentOptionsByThisOptionsSorting: newValue,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'sortParentOptionsByThisOptionsSorting',
                        newValue
                      );
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Sort Parent Selections by This Group's Sorting
                    </Typography>
                    <Tooltip
                      title={
                        <Box>
                          <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Parent Multiplier</Typography>
                          <Typography sx={{ fontSize: '12px' }}>- Modifier group must have Join Parent from Left or Join Parent from Right enabled</Typography>
                          <Typography sx={{ fontSize: '12px' }}>- Modifier group must be nested under a Fixed Price modifier group</Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Orders parent modifier selections based on the sorting defined for this option in this modifier group.
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.groupAllParentsByThisOption || false}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setFormData({
                      ...formData,
                      groupAllParentsByThisOption: newValue,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'groupAllParentsByThisOption',
                        newValue
                      );
                    }
                  }}
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Group Parent Selections by This Option
                    </Typography>
                    <Tooltip
                      title={
                        <Box>
                          <Typography sx={{ fontSize: '12px' }}>- Modifier group must be Parent Multiplier</Typography>
                          <Typography sx={{ fontSize: '12px' }}>- Modifier group must be nested under a Fixed Price modifier group</Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                    Displays parent modifier selections grouped under the options in this modifier group.
                  </Typography>
                </Box>
              }
            />
          </Box>

          {/* Additional options when canSelectMultipleOptions is enabled */}
          {formData.canSelectMultipleOptions && (
            <Box sx={{ mt: 3, pl: 2, borderLeft: '3px solid #1976d2' }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  mb: 2,
                  color: '#333',
                }}
              >
                How many times can customers select any single option?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  label="Min Any Single Options"
                  type="number"
                  value={formData.minAnySingleModifierItems ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      minAnySingleModifierItems:
                        val === '' ? null : parseInt(val),
                    });
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    handleFieldAutoSave(
                      'minAnySingleModifierItems',
                      val === '' ? null : parseInt(val)
                    );
                  }}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                />
                <TextField
                  label="Max Any Single Options"
                  type="number"
                  value={formData.maxAnySingleModifierItems ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      maxAnySingleModifierItems:
                        val === '' ? null : parseInt(val),
                    });
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    handleFieldAutoSave(
                      'maxAnySingleModifierItems',
                      val === '' ? null : parseInt(val)
                    );
                  }}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  mb: 2,
                  color: '#333',
                }}
              >
                What's the total number of options a customer can select?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Min Total Number of Modifier Items"
                  type="number"
                  value={formData.minTotalNumberOfModifierItems ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      minTotalNumberOfModifierItems:
                        val === '' ? null : parseInt(val),
                    });
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    handleFieldAutoSave(
                      'minTotalNumberOfModifierItems',
                      val === '' ? null : parseInt(val)
                    );
                  }}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                />
                <TextField
                  label="Max Total Number of Modifier Items"
                  type="number"
                  value={formData.maxTotalNumberOfModifierItems ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({
                      ...formData,
                      maxTotalNumberOfModifierItems:
                        val === '' ? null : parseInt(val),
                    });
                  }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    handleFieldAutoSave(
                      'maxTotalNumberOfModifierItems',
                      val === '' ? null : parseInt(val)
                    );
                  }}
                  onWheel={(e) => e.target.blur()}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '0' }}
                />
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              Nested Layout
            </Typography>
            <FormControl size="small" fullWidth>
              <SearchableSelect
                value={formData.showNestedLayout || 'default'}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFormData({ ...formData, showNestedLayout: newValue });
                  if (isEditing) {
                    handleFieldAutoSave('showNestedLayout', newValue);
                  }
                }}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="popup">Popup</MenuItem>
              </SearchableSelect>
            </FormControl>
            <Typography sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}>
              Choose how nested modifiers are displayed
            </Typography>
          </Box>

          {formData.priceType === 'multiplierApplyingToParentPrice' && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                In-Order View Layout
              </Typography>
              <FormControl size="small" fullWidth>
                <SearchableSelect
                  value={formData.inOrderViewLayout || ''}
                  displayEmpty
                  renderValue={(selected) => {
                    const option = IN_ORDER_VIEW_LAYOUT_OPTIONS.find(
                      (o) => o.value === selected
                    );
                    return option?.label || 'None';
                  }}
                  onChange={(e) => {
                    const newValue = e.target.value || null;
                    setFormData({ ...formData, inOrderViewLayout: newValue });
                    if (isEditing) {
                      handleFieldAutoSave('inOrderViewLayout', newValue);
                    }
                  }}
                >
                  {IN_ORDER_VIEW_LAYOUT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SearchableSelect>
              </FormControl>
              <Typography sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}>
                Controls how this modifier group is displayed in the order view
              </Typography>
              <FieldSavingIndicator
                status={fieldSavingStatus.inOrderViewLayout}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Options Section - Only show for existing modifier groups */}
      {isEditing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Options
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#9E9EAD',
                mb: 2,
              }}
            >
              Manage items that can be selected within this modifier group
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ModifierGroupOptionsManager
              modifierGroup={modifierGroup}
              menuMap={menuMap}
            />
          </CardContent>
        </Card>
      )}

      {/* Default Selections Section - Only show for existing modifier groups with options */}
      {isEditing &&
        modifierGroup?.optionIds?.length > 0 &&
        (() => {
          const mgId = modifierGroup._id;
          const selectedOptions =
            formData.preselectOptions?.options?.[mgId] || [];

          const isRadioMode = formData.min === 1 && formData.max === 1;
          const showQuantityInput =
            !isRadioMode && formData.canSelectMultipleOptions;
          const isStickyEnabled = selectedOptions.some((opt) => opt.sticky);

          const handlePreselectToggle = (optionId) => {
            let newSelectedOptions;
            const existingOption = selectedOptions.find(
              (opt) => opt._id === optionId
            );

            if (isRadioMode) {
              newSelectedOptions = existingOption
                ? []
                : [{ _id: optionId, count: 1, sticky: false }];
            } else {
              if (existingOption) {
                newSelectedOptions = selectedOptions.filter(
                  (opt) => opt._id !== optionId
                );
              } else {
                newSelectedOptions = [
                  ...selectedOptions,
                  { _id: optionId, count: 1, sticky: false },
                ];
              }
            }

            const cleanedOptions = cleanPreselectOptions(
              formData.preselectOptions?.options
            );
            const newPreselectOptions = {
              defaultSelection:
                formData.preselectOptions?.defaultSelection || false,
              options: {
                ...cleanedOptions,
                [mgId]: newSelectedOptions,
              },
            };
            setFormData({ ...formData, preselectOptions: newPreselectOptions });
            handleFieldAutoSave('preselectOptions', newPreselectOptions);
          };

          const handlePreselectQuantityChange = (optionId, count) => {
            const newSelectedOptions = selectedOptions.map((opt) =>
              opt._id === optionId ? { ...opt, count } : opt
            );
            const cleanedOptions = cleanPreselectOptions(
              formData.preselectOptions?.options
            );
            const newPreselectOptions = {
              defaultSelection:
                formData.preselectOptions?.defaultSelection || false,
              options: {
                ...cleanedOptions,
                [mgId]: newSelectedOptions,
              },
            };
            setFormData({ ...formData, preselectOptions: newPreselectOptions });
            handleFieldAutoSave('preselectOptions', newPreselectOptions);
          };

          const handlePreselectStickyToggle = () => {
            const currentSticky = selectedOptions.some((opt) => opt.sticky);
            const newSelectedOptions = selectedOptions.map((opt) => ({
              ...opt,
              sticky: !currentSticky,
            }));
            const cleanedOptions = cleanPreselectOptions(
              formData.preselectOptions?.options
            );
            const newPreselectOptions = {
              defaultSelection:
                formData.preselectOptions?.defaultSelection || false,
              options: {
                ...cleanedOptions,
                [mgId]: newSelectedOptions,
              },
            };
            setFormData({ ...formData, preselectOptions: newPreselectOptions });
            handleFieldAutoSave('preselectOptions', newPreselectOptions);
          };

          const handlePreselectQuantityInput = (optionId, val) => {
            if (val === '' || parseInt(val) === 0) {
              const newSelectedOptions = selectedOptions.filter(
                (opt) => opt._id !== optionId
              );
              const newPreselectOptions = {
                ...formData.preselectOptions,
                options: {
                  ...formData.preselectOptions?.options,
                  [mgId]: newSelectedOptions,
                },
              };
              setFormData({
                ...formData,
                preselectOptions: newPreselectOptions,
              });
              handleFieldAutoSave('preselectOptions', newPreselectOptions);
            } else {
              const existingOption = selectedOptions.find(
                (opt) => opt._id === optionId
              );
              if (existingOption) {
                handlePreselectQuantityChange(optionId, parseInt(val));
              } else {
                const newSelectedOptions = [
                  ...selectedOptions,
                  { _id: optionId, count: parseInt(val), sticky: false },
                ];
                const newPreselectOptions = {
                  ...formData.preselectOptions,
                  options: {
                    ...formData.preselectOptions?.options,
                    [mgId]: newSelectedOptions,
                  },
                };
                setFormData({
                  ...formData,
                  preselectOptions: newPreselectOptions,
                });
                handleFieldAutoSave('preselectOptions', newPreselectOptions);
              }
            }
          };

          return (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Default Selections
                </Typography>
                <Typography variant="body2" sx={{ color: '#9E9EAD', mb: 2 }}>
                  Pre-select options for this modifier group
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          formData.preselectOptions?.defaultSelection || false
                        }
                        onChange={(e) => {
                          const cleanedOptions = cleanPreselectOptions(
                            formData.preselectOptions?.options
                          );
                          const newPreselectOptions = {
                            defaultSelection: e.target.checked,
                            options: cleanedOptions,
                          };
                          setFormData({
                            ...formData,
                            preselectOptions: newPreselectOptions,
                          });
                          handleFieldAutoSave(
                            'preselectOptions',
                            newPreselectOptions
                          );
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Enable Default Selections
                      </Typography>
                    }
                  />
                  <FieldSavingIndicator
                    status={fieldSavingStatus.preselectOptions}
                  />
                </Box>

                {formData.preselectOptions?.defaultSelection && (
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        mb: 2,
                        textTransform: 'uppercase',
                        color: '#666',
                      }}
                    >
                      {getTranslationValue(modifierGroup.name)}
                    </Typography>

                    {modifierGroup.optionIds.map((optionId) => {
                      const optionItem = menuMap?.items?.[optionId];
                      if (!optionItem) return null;

                      const isSelected = selectedOptions.some(
                        (opt) => opt._id === optionId
                      );
                      const selectedOption = selectedOptions.find(
                        (opt) => opt._id === optionId
                      );
                      const optionName =
                        getTranslationValue(optionItem.name) || 'Option';

                      return (
                        <Box
                          key={optionId}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          {isRadioMode ? (
                            <FormControlLabel
                              control={
                                <Radio
                                  checked={isSelected}
                                  onClick={() =>
                                    handlePreselectToggle(optionId)
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '14px' }}>
                                  {optionName}
                                </Typography>
                              }
                            />
                          ) : showQuantityInput ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flex: 1,
                              }}
                            >
                              <Typography sx={{ fontSize: '14px', flex: 1 }}>
                                {optionName}
                              </Typography>
                              <TextField
                                size="small"
                                type="number"
                                label="Qty"
                                value={selectedOption?.count || ''}
                                onChange={(e) =>
                                  handlePreselectQuantityInput(
                                    optionId,
                                    e.target.value
                                  )
                                }
                                onWheel={(e) => e.target.blur()}
                                inputProps={{ min: 0 }}
                                sx={{ width: 80 }}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Box>
                          ) : (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() =>
                                    handlePreselectToggle(optionId)
                                  }
                                  size="small"
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: '14px' }}>
                                  {optionName}
                                </Typography>
                              }
                            />
                          )}
                        </Box>
                      );
                    })}

                    {selectedOptions.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isStickyEnabled}
                            onChange={handlePreselectStickyToggle}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography sx={{ fontSize: '13px' }}>
                              Sticky selections
                            </Typography>
                            <Typography
                              sx={{ fontSize: '11px', color: '#9E9EAD' }}
                            >
                              Prevent customers from changing these preselected
                              options
                            </Typography>
                          </Box>
                        }
                        sx={{ mt: 2 }}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })()}

      {/* Connected Products Section - Only show for existing modifier groups */}
      {isEditing &&
        (() => {
          const connectedItems = Object.values(menuMap?.items || {}).filter(
            (menuItem) =>
              menuItem.modifierGroupIds?.includes(modifierGroup?._id)
          );

          const itemsToExclude = getMgDescendants(modifierGroup?._id, menuMap);

          const allProducts = Object.values(menuMap?.items || {})
            .filter((item) => !itemsToExclude.has(item._id))
            .sort((a, b) => {
              const nameA = getTranslationValue(a.name)?.toLowerCase() || '';
              const nameB = getTranslationValue(b.name)?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });

          const connectedProductIds = new Set(
            connectedItems.map((item) => item._id)
          );

          return (
            <>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6">
                        Connected Products ({connectedItems.length})
                      </Typography>
                      {isUpdatingProducts && <CircularProgress size={20} />}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPickerOpen(true)}
                      disabled={isUpdatingProducts}
                    >
                      Manage Products
                    </Button>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#9E9EAD', mb: 1.5 }}
                  >
                    Products that use this modifier group
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  {connectedItems.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        py: 2,
                        textAlign: 'center',
                      }}
                    >
                      No products connected yet. Click &quot;Manage
                      Products&quot; to add some.
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        maxHeight: 200,
                        overflowY: 'auto',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        py: 0.5,
                      }}
                    >
                      {connectedItems
                        .sort((a, b) => {
                          const nameA =
                            getTranslationValue(a.name)?.toLowerCase() || '';
                          const nameB =
                            getTranslationValue(b.name)?.toLowerCase() || '';
                          return nameA.localeCompare(nameB);
                        })
                        .map((item) => {
                          const name = getTranslationValue(item.name);
                          const title = getTranslationValue(item.title);
                          const label = title ? `${name} (${title})` : name;
                          return (
                            <Chip
                              key={item._id}
                              label={label}
                              size="small"
                              onDelete={() => handleQuickRemove(item._id)}
                              disabled={isUpdatingProducts}
                            />
                          );
                        })}
                    </Box>
                  )}
                </CardContent>
              </Card>

              <ConnectedProductsPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                allProducts={allProducts}
                connectedProductIds={connectedProductIds}
                categories={menuMap?.categories || {}}
                modifierGroups={menuMap?.modifierGroups || {}}
                onSave={handlePickerSave}
                isSaving={isUpdatingProducts}
              />
            </>
          );
        })()}

      {/* Info for new modifier groups */}
      {!isEditing && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            p: 1.5,
            bgcolor: '#e3f2fd',
            borderRadius: 1,
            color: 'primary.dark',
          }}
        >
          Note: You can add options to this modifier group after creation.
        </Typography>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "
            {formData.name?.en || 'this modifier group'}"? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            color="primary"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModifierGroupEdit;
